import { StatRow, StatProps, TextVariant, Icon, Text, UsdPriceTextWithTooltip } from "@bridgesplit/ui";
import { formatNum } from "@bridgesplit/utils";
import { useUserPoints } from "@bridgesplit/abf-react";

import { Points } from "../../common";
import { COPY } from "../../../constants";
import { useActivePortfolioRewardsSummary } from "./util";
import { PortfolioAggregates } from "./usePortfolioAggregates";

const textColor = "body";

export default function PortfolioOverviewStats({
    variant,
    aggregates
}: {
    variant: TextVariant;
    aggregates: PortfolioAggregates;
}) {
    const { netValue, dailyNetInterestUsd, isLoading } = aggregates;
    const { getPointsAtTime, isLoading: pointsLoading } = useUserPoints();

    const stats: StatProps[] = [
        {
            value: <UsdPriceTextWithTooltip textColor={textColor} rawUsdValue={netValue} loading={isLoading} />,
            caption: COPY.NET_VALUE_TERM
        },
        {
            value: (
                <UsdPriceTextWithTooltip textColor={textColor} rawUsdValue={dailyNetInterestUsd} loading={isLoading} />
            ),
            caption: COPY.NET_INCOME_TERM,
            tooltip: COPY.PORTFOLIO_DAILY_INTEREST_TOOLTIP
        },
        {
            value: (
                <Text color={textColor} svgColor="secondary">
                    <Icon type="points" />
                    <Points getPoints={getPointsAtTime} />
                </Text>
            ),
            caption: COPY.POINTS_TERM,
            tooltip: "Points are earned gradually as you interact with the protocol"
        }
    ];

    return <StatRow captionVariant="body2" variant={variant} loading={isLoading || pointsLoading} stats={stats} />;
}

export function useRewardBreakdownStats({ variant }: { variant: TextVariant }) {
    const { data: portfolioRewards, isLoading: isLoadingActivePortfolioRewards } = useActivePortfolioRewardsSummary();

    const rewardBreakdown: StatProps[] = [
        {
            value: <RewardPoint points={portfolioRewards?.activeLendVaultDailyPoints ?? 0} variant={variant} />,
            caption: "Lend / day"
        },
        {
            value: <RewardPoint points={portfolioRewards?.activeEarnVaultDailyPoints ?? 0} variant={variant} />,
            caption: "Earn vault / day"
        },
        {
            value: <RewardPoint points={portfolioRewards?.activeLoopDailyPoints ?? 0} variant={variant} />,
            caption: "Loop / day"
        },
        {
            value: <RewardPoint points={portfolioRewards?.activeBorrowDailyPoints ?? 0} variant={variant} />,
            caption: "Borrow / day"
        },
        {
            value: <RewardPoint points={portfolioRewards?.activeStrategyDailyLendPoints ?? 0} variant={variant} />,
            caption: "Advanced lend / day"
        }
    ];

    return { rewardBreakdown, isLoading: isLoadingActivePortfolioRewards };
}

function RewardPoint({ points, variant }: { points: number; variant: TextVariant }) {
    return (
        <Text svgColor="secondary" variant={variant}>
            <Icon type="points" />
            {formatNum(points)}
        </Text>
    );
}
